import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DeviceConnection } from "../../../api/deviceConnection";
import { LicenseOption } from "../../../api/license";
import { DeviceWithoutLegacyPassword } from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { remoteDeviceRoute } from "../../../utils/roles";
import { Button } from "../../common/Button";
import { DisplayValue } from "../../common/DisplayValue";
import { LabelParam } from "../../common/LabelParam";
import { TitleSection } from "../../common/TitleSection";
import {
  ConnectionDot,
  connectionLabel,
  isRemoteBlocked,
} from "./ConnectionDot";
import { DeviceTypeBadge } from "./DeviceTypeBadge";
import { LegacyPacksSection, LegacyPacksState } from "./LegacyPacks";
import { LicenseSection } from "./LicenseModal";
import { SerenityConfigsSection } from "./SerenityConfigs";

/**
 * « mini » et « default » : le produit dans une liste. « maxi » : la fiche produit (admin / PSC),
 * ce qu'on consulte le plus (identité, IP) en haut, le détail technique replié, puis licences et
 * sauvegardes. `device.status` n'est pas affiché : il reste « active » à vie, la connexion VPN
 * le remplace.
 */
export const DeviceSection = ({
  device,
  detailLevel = "default",
  onClick,
  onLicenseOpen,
  licenses = [],
  legacyPacks,
  connection,
}: {
  device: DeviceWithoutLegacyPassword;
  detailLevel?: "default" | "mini" | "maxi";
  onClick?: () => void;
  onLicenseOpen?: () => void;
  licenses?: LicenseOption[];
  /** Packs d'un produit legacy, en lecture seule (aucun bouton de modification). */
  legacyPacks?: LegacyPacksState;
  connection?: DeviceConnection;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAccountStorage } = useAccountStorage();

  if (detailLevel !== "maxi") {
    return (
      <div>
        <div className="flex justify-between border-b border-grey-light mb-4 ">
          <button
            className={`${detailLevel === "mini" ? "text-sm" : "text-base"} font-oxaniumMedium ${
              onClick ? "hover:text-tertiary cursor-pointer" : "cursor-default"
            }`}
            onClick={onClick}
          >
            <div className="flex items-center gap-2">
              <DeviceTypeBadge type={device.type} />
              <span>- {device.name}</span>
            </div>
          </button>
        </div>
        {detailLevel === "default" && (
          <div className="space-y-4">
            <LabelParam label={t("device-type")}>
              <DeviceTypeBadge type={device.type} />
            </LabelParam>
            <LabelParam label={t("device-name")}>
              <DisplayValue value={device.name ?? "-"} />
            </LabelParam>
            <LabelParam label={t("device-version")}>
              <DisplayValue value={device.version ?? "-"} />
            </LabelParam>
          </div>
        )}
      </div>
    );
  }

  const remoteBlocked = isRemoteBlocked(connection);
  const remoteRoute = remoteDeviceRoute(device.uuid, getAccountStorage()?.type);
  const isSerenity = device.type === "serenity";

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center gap-3 border-b border-grey-light pb-2">
          <DeviceTypeBadge type={device.type} />
          <span className="flex-1 text-lg font-oxaniumMedium">
            {device.name || "-"}
          </span>
          <ConnectionDot connection={connection} />
        </div>

        <LabelParam label={t("device-version")}>
          <DisplayValue value={device.version || "-"} />
        </LabelParam>
        <LabelParam label={t("device-uuid")}>
          <DisplayValue value={device.uuid || "-"} />
        </LabelParam>
        <LabelParam label={t("device-ip-vpn")}>
          <DisplayValue value={device.ip_vpn || "-"} />
        </LabelParam>
        <LabelParam label={t("device-ip-local")}>
          <DisplayValue value={device.ip_local || "-"} />
        </LabelParam>

        {remoteRoute && (
          <div className="space-y-1">
            <Button
              type="button"
              variant="tertiary"
              size="full"
              className={`text-sm ${remoteBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={remoteBlocked}
              onClick={() => navigate(remoteRoute)}
            >
              <FontAwesomeIcon icon="eye" className="mr-2" />
              {t("device-view")}
            </Button>
            {remoteBlocked && (
              <p className="text-xs text-grey-dark">
                {t("remote-view-blocked-disconnected")}
              </p>
            )}
          </div>
        )}

        <details className="group">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-oxaniumMedium text-grey-dark hover:text-tertiary">
            <FontAwesomeIcon
              icon="chevron-right"
              className="h-3 w-3 transition-transform group-open:rotate-90"
            />
            {t("device-technical-details")}
          </summary>
          <div className="mt-4">
            <LabelParam label={t("device-connection")}>
              <DisplayValue value={connectionLabel(t, connection)} />
            </LabelParam>
            <LabelParam label={t("device-kernel-version")}>
              <DisplayValue value={device.kernel_version || "-"} />
            </LabelParam>
            <LabelParam label={t("device-adresse-mac")}>
              <DisplayValue value={device.adresse_mac || "-"} />
            </LabelParam>
            {/* Optimo+ : pas encore d'API, d'où les tirets. */}
            {isSerenity && (
              <>
                <LabelParam label={t("optimo-uuid")}>
                  <DisplayValue value="-" />
                </LabelParam>
                <LabelParam label={t("optimo-ip")}>
                  <DisplayValue value="-" />
                </LabelParam>
                <LabelParam label={t("optimo-version")}>
                  <DisplayValue value="-" />
                </LabelParam>
              </>
            )}
          </div>
        </details>
      </div>

      {isSerenity && (
        <div>
          <TitleSection
            title={t("device-licenses")}
            icon="lock"
            onClick={onLicenseOpen}
            actionIcon="pen"
            actionLabel={t("device-licenses-manage")}
          />
          <LicenseSection activeCodes={licenses} />
        </div>
      )}
      {legacyPacks && (
        <div>
          <TitleSection title={t("device-licenses")} icon="lock" />
          <LegacyPacksSection state={legacyPacks} />
        </div>
      )}

      {isSerenity && <SerenityConfigsSection uuid={device.uuid} />}
    </div>
  );
};
